import { Fund } from './../api/robotapi';
import { DateTime, DurationInput, Interval } from 'luxon';
import { GetFundRatesResponse } from '../api/funds-robot/getFundRates';
import { IntervalType } from '../types/IntervalType';
import { TimeValueDataPoint } from '../components/funds-robot/comparison-screen/comparison-chart/ValueGrowthChart';
import { DividendPayment } from '../types/Stock';
import { toPercentage } from './parsers';
import { MonthlyReturnRate } from '../api/funds-robot/getModelPortfolioMonthlyReturns';
import { PensionParameters } from '../contexts/pension-robot/PensionContext';
import {
    ForecastParameters,
    getSearchParamsFromForecastParameters,
} from '../contexts/investment-robot/ForecastContext';
import { GOAL } from '../contexts/investment-robot/GoalIndicationContext';
import { CLIENT_DETAILS_OPTIONS, RiskProfileId, riskProfileIds, ROBOT_ENVIRONMENT } from '../config/constants';
import { GENERAL_PAGES, GoalPage, postFixDetailsPage, preFixDetailsPage } from '../config/goals';
import { ForecastRetirement } from '../api/pension-robot/useGetForecastRetirement';
import { LateEarlyRetirementFactor } from '../api/pension-robot/useGetPensionCalcData';

// Copies a string to the clipboard. Must be called from within an
// event handler such as click. May return false if it failed, but
// this is not always possible. Browser support for Chrome 43+,
// Firefox 42+, Safari 10+, Edge and Internet Explorer 10+.
// Internet Explorer: The clipboard feature may be disabled by
// an administrator. By default a prompt is shown the first
// time the clipboard is used (per session).
export function copyToClipboard(text: string) {
    if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
        var textarea = document.createElement('textarea');
        textarea.textContent = text;
        textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in Microsoft Edge.
        document.body.appendChild(textarea);
        textarea.select();
        try {
            return document.execCommand('copy'); // Security exception may be thrown by some browsers.
        } catch (ex) {
            console.warn('Copy to clipboard failed.', ex); // eslint-disable-line
            return false;
        } finally {
            document.body.removeChild(textarea);
        }
    }
}

export const roundFunds = (funds: Fund[]): Fund[] =>
    funds.map((share: Fund, index: number) => {
        let weight = Math.round(share.weight * 1000) / 10;

        if (index === 0) {
            // First item in the array
            const totals = funds.reduce(
                (totals, share, _index) => {
                    return {
                        total: totals.total + share.weight * 100,
                        precedingRounded:
                            _index === index
                                ? totals.precedingRounded
                                : totals.precedingRounded + Math.round(share.weight * 1000) / 10,
                    };
                },
                { total: 0, precedingRounded: 0 },
            );

            weight = Math.round(totals.total) - totals.precedingRounded;
        }
        return { ...share, weight };
    });

export const randomNegative = () => (Math.random() < 0.5 ? -1 : 1);

export const simulateRandomData = () => {
    // start simulation at 100
    let base = 100;

    const nextVal = () => {
        // with quadratic Math.random() you can go for a higher scaling factor
        //      but reduce the chance that those 'spikes' happen
        let ret = base;
        base = Math.abs(base + randomNegative() * Math.random() * Math.random() * 20);
        return ret;
    };
    return nextVal;
};

export const simulateRandomIncreasingData = () => {
    // start simulation at 0
    let base = 0;

    const nextVal = () => {
        // with quadratic Math.random() you can go for a higher scaling factor
        //      but reduce the chance that those 'spikes' happen
        let ret = base;
        base = Math.round(Math.abs(base + Math.random() * Math.random() * 1000) * 100) / 100;
        return ret;
    };
    return nextVal;
};

export function withDate(value: number, index: number, array: number[], endDate: DateTime) {
    return {
        t: endDate
            .startOf('day')
            .minus({ days: array.length - (index + 1) })
            .toMillis(),
        y: value,
    };
}

export function withMonth(value: number, index: number, array: number[], endDate: DateTime) {
    return {
        t: endDate
            .startOf('month')
            .minus({ month: array.length - (index + 1) })
            .toMillis(),
        y: value,
    };
}

export function genArray<T>(N: number, f: (index: number) => T) {
    return Array.from(Array(N).keys()).map(f);
}

export const getComparableChunk = <R extends { date: string }>(
    rates: { id: string; rates: R[] }[],
): { id: string; rates: R[] }[] => {
    // We may only use rates within a timeframe wherein all responses have rates.
    if (rates.length === 0) return [];

    // Sorting the rates DESC (NEW to OLD).
    let _rates = [...rates].map((r) => {
        return {
            id: r.id,
            rates: [...r.rates].sort((r1, r2) => {
                const r1Millies = DateTime.fromISO(r1.date).toMillis();
                const r2Millies = DateTime.fromISO(r2.date).toMillis();

                return r1Millies < r2Millies ? 1 : r1Millies > r2Millies ? -1 : 0;
            }),
        };
    });

    const actualEndDate = _rates.reduce((endDate, currentFundRateResponse) => {
        const currentDateTime = DateTime.fromISO(currentFundRateResponse.rates[0].date);
        if (endDate.toMillis() > currentDateTime.toMillis()) return currentDateTime;
        return endDate;
    }, DateTime.fromISO(_rates[0].rates[0].date));

    const actualStartDate = _rates.reduce((startDate, currentFundRateResponse) => {
        const currentDateTime = DateTime.fromISO(
            currentFundRateResponse.rates[currentFundRateResponse.rates.length - 1].date,
        );
        if (startDate.toMillis() < currentDateTime.toMillis()) return currentDateTime;
        return startDate;
    }, DateTime.fromISO(_rates[0].rates[_rates[0].rates.length - 1].date));

    return _rates.map((r) => {
        return {
            id: r.id + '',
            rates: r.rates.filter((r) => {
                const date = DateTime.fromISO(r.date);
                return date.toMillis() >= actualStartDate.toMillis() && date.toMillis() <= actualEndDate.toMillis();
            }),
        };
    });
};

/** Adds a relative NAV to the rates. Dividends are the dividends within interval. Works for Stocks and Bonds. */
export const indexedFundTimeValueDataPoints = (
    dataPoints: TimeValueDataPoint[],
    dividends: { date: DateTime; amount: number }[],
) => {
    if (dataPoints.length === 0) return [];

    const earliestDataPoint = dataPoints[dataPoints.length - 1];
    const normalisationFactor = 100 / earliestDataPoint.y;

    // Inital values as first value doesn't have preceding datapoints to be based on
    var previousDate = DateTime.fromMillis(earliestDataPoint.t);
    var previousNAVRate = earliestDataPoint.y;
    var previousCalculatedNAV = earliestDataPoint.y + allDividendsInIntervalIncludingStart(
        dividends,
        Interval.fromDateTimes(previousDate, previousDate),
    );

    // dataPoints is sorted to go back in time, while we need to walk through them 
    // in chronological order, as future values are based on older values. So reverse
    const processedDataPoints = [...dataPoints].slice(0).reverse().map((dp) => {
        const addedDividends = allDividendsInIntervalIncludingStart(
            dividends,
            Interval.fromDateTimes(previousDate, DateTime.fromMillis(dp.t)),
        );

        const calculatedNAV = previousCalculatedNAV * ((dp.y + addedDividends) / previousNAVRate);
        const normalizedRate = normalisationFactor * calculatedNAV;

        previousCalculatedNAV = calculatedNAV;
        previousNAVRate = dp.y;
        previousDate = DateTime.fromMillis(dp.t);
        return {
            t: dp.t,
            y: normalizedRate,
        };
    });

    // Reverse back as graph requires the data to be in the original order
    return processedDataPoints.reverse();
};

/** Returns all dividends that are paid out before a timestamp, aggregated. */
const allDividendsInIntervalIncludingStart = (dividends: { date: DateTime; amount: number }[], interval: Interval) => {
    return dividends
        .filter((d) => {
            // Allows all dividends that are paid out in this interval.
            // TODO: can we assume here start and end exist?
            return d.date.toMillis() < interval!.end!.toMillis() && d.date.toMillis() >= interval!.start!.toMillis();
        })
        .reduce((total, current) => {
            return total + current.amount;
        }, 0);
};

/** Adds a relative return to the rates. Only works for modelportfolios. Expects the rates to be sorted from NEW to OLD. */
export const indexedPortfolioTimeValueDataPoints = (dataPoints: TimeValueDataPoint[]) => {
    if (dataPoints.length === 0) return [];
    let base = 100;

    // Reverse; original array is chronological DESC, we want ASC now.
    const _dataPoints = [...dataPoints].sort((t1, t2) => (t1.t > t2.t ? 1 : t2.t > t1.t ? -1 : 0)); // ASC sorting

    const indexed = _dataPoints.map((point, i) => {
        base = base * (point.y + 1);
        return {
            t: point.t,
            y: base,
        };
    });

    const result = indexed.reverse();
    const firstDataPointAsDateTime = DateTime.fromMillis(dataPoints[dataPoints.length - 1].t)

    return [...result, {
        t: firstDataPointAsDateTime.startOf('month').toMillis(),
        y: 100
    }]
};

/** Creates an interval based on the interval type. If the rates are given, the interval end date will be the same as the last known rate, otherwise now. Expects the rates to be sorted chronologically DESC. */
export const getInterval = (interval: Omit<IntervalType, 'Custom'>) => {
    const current = DateTime.now().startOf('day');

    switch (interval) {
        case '1M':
            return Interval.before(current, { months: 1 });
        case '1Y':
            return Interval.before(current, { years: 1 });
        case '5Y':
            return Interval.before(current, { years: 5 });
        case '10Y':
            return Interval.before(current, { years: 10 });
        default:
            // Defaults to 5Y
            return Interval.before(current, { years: 5 });
    }
};

export const getCustomInterval = (customEndDate: DateTime, customInterval: number) => {
    const custom = customEndDate.startOf('day');
    const interval = { days: customInterval };
    return Interval.before(custom, interval);
};

/**
 * Filters the data on the shown data. When interval > 2 years, than only show monthly data. Else, when interval > 2 months, show only weekly data. Otherwise show all data (daily).
 * @param data Chart dataset
 * 
 * @returns Filtered data as explained.
 */
export const thinnedOutOnDate = (data: TimeValueDataPoint[]) => {
    if (data.length === 0) return data;

    const latestDate = DateTime.fromMillis(data[0].t);
    const earliestDate = DateTime.fromMillis(data[data.length - 1].t);
    const dateDiff = latestDate.diff(earliestDate, ['years', 'months']);

    const groupOn = (check: (some: TimeValueDataPoint, other: TimeValueDataPoint) => boolean) => {
        var groupedData = [] as TimeValueDataPoint[][];
        var offset = 0;
        for (let index = 0; index < data.length; index++) {
            if (!check(data[offset], data[index])) {
                groupedData.push(data.slice(offset, index));
                offset = index;
            };
        }
        groupedData.push(data.slice(offset, data.length));

        return groupedData
    }

    const differentMonthCheck = (some: TimeValueDataPoint, other: TimeValueDataPoint) => {
        const someTime = DateTime.fromMillis(some.t);
        const otherTime = DateTime.fromMillis(other.t);
        return someTime.month === otherTime.month;
    }

    const differentWeekCheck = (some: TimeValueDataPoint, other: TimeValueDataPoint) => {
        const someTime = DateTime.fromMillis(some.t);
        const otherTime = DateTime.fromMillis(other.t);
        return someTime.weekNumber === otherTime.weekNumber;
    }

    const getOneDatapointPerGroup = (groupedDataPoints: TimeValueDataPoint[][]) => {
        const indexOfEarsliestGroup = groupedDataPoints.length - 1;

        return groupedDataPoints.map((dataPoints, index) => {
            if (index === indexOfEarsliestGroup)
                return dataPoints[dataPoints.length - 1]
            return dataPoints[0];
        })
    }

    if (dateDiff.years >= 2) {
        const groupedPerMonth = groupOn(differentMonthCheck);
        return getOneDatapointPerGroup(groupedPerMonth);
    }

    if (dateDiff.months > 2 || dateDiff.years >= 1) {
        const groupedPerWeek = groupOn(differentWeekCheck);
        return getOneDatapointPerGroup(groupedPerWeek);
    }

    return data;
};

export const timeValueDataPointsWithinInterval = (data: TimeValueDataPoint[], interval: Interval) => {
    return [...data].filter((d) => {
        // Interval.contains doesn't work, because it fails when both the interval.end and the compared date are the same.
        // TODO: can we assume here start and end exist?
        return d.t >= interval!.start!.toMillis() && d.t <= interval!.end!.toMillis();
    });
};

export const dividendsWithinInterval = (dividends: { date: DateTime; amount: number }[], interval: Interval) => {
    return [...dividends].filter((d) => {
        return interval.contains(d.date);
    });
};

/** Gets the interval based on the first data point and last data point. If the data does not cover the whole interval, add at least a month to the interval to indicate a gap in data. */
export const getLabelInterval = (
    chosenInterval: Interval,
    data: TimeValueDataPoint[],
    offset: DurationInput = { months: 1 },
) => {
    if (data.length === 0) return chosenInterval;
    const mostRecentDataPoint = DateTime.fromMillis(data[0].t);
    const oldestDataPoint = DateTime.fromMillis(data[data.length - 1].t);

    if (chosenInterval.start === null) {
        throw Error(`Invalid interval start: ${data.length}`);
    }

    if (oldestDataPoint.diff(chosenInterval.start, 'days').days > 31) {
        return Interval.fromDateTimes(oldestDataPoint.minus(offset), mostRecentDataPoint);
    }

    return Interval.fromDateTimes(chosenInterval.start, mostRecentDataPoint);
};

export const generateLabels = (interval: Interval, labelAmount: number = 4): number[] => {
    const intervals = interval.divideEqually(labelAmount);
    // TODO: can we assume here start and end exist?
    return [...intervals.map((i) => i!.start!.toMillis()), intervals[intervals.length - 1]!.end!.toMillis()];
};

export const mergedItems = (
    sections: { id: string; name: string; weight: number }[][],
    currentSection: { id: string; name: string; weight: number }[],
    filterAllZeros = true,
) => {
    if (currentSection.length === 0) return [];

    const mergedItems = sections.reduce<{ name: string; id: string }[]>((total, current) => {
        const newItems = current.filter(({ id }) => {
            return total.find((t) => t.id === id) === undefined;
        });
        return [...total, ...newItems];
    }, []);

    return mergedItems
        .map((mergedItem) => {
            const item = currentSection.find((i) => i.id === mergedItem.id);

            return item ? item : { ...mergedItem, weight: 0 };
        })
        .sort((i1, i2) => {
            return Number(i1.id) > Number(i2.id) ? 1 : Number(i1.id) < Number(i2.id) ? -1 : 0;
        })
        .filter((mergedItem) => {
            if (!filterAllZeros) return true;
            return !sections.reduce<boolean>((shouldFilter, items) => {
                const item = items.find((i) => i.id === mergedItem.id);
                return shouldFilter && (item?.weight || 0) < 0.01;
            }, true);
        });
};

export interface NAVCSVLine {
    nav: number;
    ask: number;
    bid: number;
    date: DateTime;
    dividend?: number;
}

export const ratesAndDividendToNAVCSVString = (
    rates: GetFundRatesResponse['rates'],
    dividends: DividendPayment[],
): string => {
    const data = ratesAndDividendToNAVCSVLines(rates, dividends);

    return buildNAVCSVString(data);
};

export const ratesAndDividendToNAVCSVLines = (
    rates: GetFundRatesResponse['rates'],
    dividends: DividendPayment[],
): NAVCSVLine[] => {
    const data = rates.map((rate) => {
        const rateDate = DateTime.fromISO(rate.date);

        const dividend = dividends.find((d) => {
            const dividendDate = DateTime.fromISO(d.date);

            return (
                rateDate.day === dividendDate.day &&
                rateDate.month === dividendDate.month &&
                rateDate.year === dividendDate.year
            );
        });

        return {
            date: rateDate,
            ask: rate.askPrice,
            bid: rate.bidPrice,
            nav: rate.nav,
            dividend: dividend?.amountPerParticipation ? dividend?.amountPerParticipation / 100 : undefined,
        };
    });

    return data;
};

export const buildNAVCSVString = (data: NAVCSVLine[]) => {
    const formatted = [
        ['Date', 'NAV', 'Ask', 'Bid', 'Dividend'].join(';'),
        ...data.map(({ nav, ask, bid, date, dividend }) =>
            [
                date.toFormat('d/M/yyyy'),
                nav.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
                ask.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
                bid.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
                dividend
                    ? dividend.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
                    : '-',
            ].join(';'),
        ),
    ];

    return formatted.join('\n');
};

export const buildMonthlyReturnRatesCSVString = (data: MonthlyReturnRate[]) => {
    return [
        ['Datum', 'Rendement'].join(';'),
        ...data
            .sort((r1, r2) => {
                const r1ms = DateTime.fromISO(r1.date).toMillis();
                const r2ms = DateTime.fromISO(r2.date).toMillis();
                return r1ms < r2ms ? 1 : r1ms > r2ms ? -1 : 0;
            })
            .map((rate) => {
                return [DateTime.fromISO(rate.date).toFormat('d/M/yyyy'), toPercentage(rate.return * 100)].join(';');
            }),
    ].join('\n');
};

export const makeCSVBlob = (data: string): Blob => new Blob([data], { type: 'text/csv;charset=utf-8' });

export const getSelectableYears = (data: GetFundRatesResponse | undefined) => {
    let years: number[] = [];
    if (!!data) {
        let startYear = DateTime.fromISO(data.rates[data.rates.length - 1].date).year;
        const endYear = DateTime.now().year;
        while (startYear <= endYear) {
            years = years.concat(startYear);
            startYear = startYear + 1;
        }
    }
    return years;
};

export function downloadCSV(filename: string, contents: string) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(makeCSVBlob(contents));
    link.download = filename;
    link.click();
}

export function isPensionCalculatedTogether(params: PensionParameters['personalInfo']): boolean {
    return params.maritalStatus !== 'single' && !!params.isCalculatedTogether;
}
export const getInvestmentToolDirectLink = (parameters: ForecastParameters, selectedGoal?: GOAL) => {
    const link =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname +
        '?' +
        getSearchParamsFromForecastParameters(parameters, selectedGoal).toString();
    return link;
};

export const getPensionToolDirectLinkWithToken = (userToken: string | undefined) => {
    const url = window.location.origin + window.location.pathname
    if (userToken === undefined)
        return url

    const link = url + `?userToken=${userToken}`;
    return link;
};


export const getGeneralPages = (contactDetailsOption: CLIENT_DETAILS_OPTIONS | undefined) => {
    if (contactDetailsOption === undefined)
        return GENERAL_PAGES;

    if (contactDetailsOption === 'last')
        return postFixDetailsPage(GENERAL_PAGES);
    return GENERAL_PAGES;
}

export const getGoalSpecificPages = (pages: GoalPage[], contactDetailsOption: CLIENT_DETAILS_OPTIONS | undefined) => {
    if (contactDetailsOption === undefined)
        return pages;

    if (contactDetailsOption === 'first')
        return preFixDetailsPage(pages);
    return pages;
}

// Calculated as specified by BND
export const getAnnualReturn = (totalReturnPercentage: number, interval: Interval) => {
    const yearFrac = interval.length('day') / 365;
    return (Math.pow((1 + (totalReturnPercentage / 100)), (1 / yearFrac)) - 1) * 100;
};

export const getSavingUntilPensionAge = (
    amount: number,
    periodInYears: number,
    yearlyMarketRate: number
) => {
    const returnPercentage = Math.pow((1 + yearlyMarketRate), periodInYears);
    return returnPercentage * amount;
}

export const getResultSavingsAccount = (
    currentValue: number,
    paymentRecurringAmount: number,
    paymentRecurringInterval: 'month' | 'year',
    yearsToAccountEnd: number,
    yearlyMarketRate: number,
    paymentRecurringDuration?: number,
    monthOfPayment: number = 0
) => {

    let res = currentValue;
    const monthlyReturnRatePerMonth = Math.pow((1 + yearlyMarketRate), (1 / 12)) - 1;
    const durationInMonths = yearsToAccountEnd * 12;

    for (let i: number = 1; i <= durationInMonths; i++) {
        if (paymentRecurringDuration === undefined || i <= paymentRecurringDuration) {
            if (paymentRecurringInterval === 'month') {
                res += paymentRecurringAmount;
            }
            if (paymentRecurringInterval === 'year' && i % 12 === 1) {
                res += paymentRecurringAmount;
            }
        }
        res += res * monthlyReturnRatePerMonth;
    }
    return res;
};

export const isMyBND = (env: ROBOT_ENVIRONMENT) => {
    return env === 'MyBND';
}; 

export const getPensionDate = (retirementAge: number, currentAge: number) =>
    (DateTime.now().plus({ years: Math.max(retirementAge - currentAge, 0) })).toISODate();

export const riskProfileById = (id: RiskProfileId) =>
    riskProfileIds[id];
    
export const getPerMonthFactor = (retirementForecast?: ForecastRetirement) => {
    const res = retirementForecast
        ? ((retirementForecast.valuesOnInvestmentEndDate?.median ?? 0)
            / (retirementForecast.monthlyRetirementValues?.median ?? 1))
        : 0;
    return res;
};

export const calculatePensionLateEarlyRetirement = (pensionAmount: number, legalRetirementAge: number, userRetirementAge: number, lateEarlyRetirementFactors: LateEarlyRetirementFactor[]) => {
    if (userRetirementAge === legalRetirementAge) return pensionAmount;

    let res = pensionAmount;
    const currentAge = userRetirementAge < legalRetirementAge ? userRetirementAge : legalRetirementAge;
    const plusMinusFactor = userRetirementAge < legalRetirementAge ? -1 : 1;

    for (let i = 1; i <= (Math.abs(userRetirementAge - legalRetirementAge)); i++) {
        const factor = userRetirementAge < legalRetirementAge
            ? (lateEarlyRetirementFactors.find(x => x.targetAge === legalRetirementAge - i)?.earlyFactor ?? 0)
            : (lateEarlyRetirementFactors.find(x => x.targetAge === currentAge + i)?.lateFactor ?? 0)
        res = res * (1 + (plusMinusFactor * factor));
    }
    return res;
};

export const isGoalAchieved = (goal: number, income: number) => {
    let achieved = false;
    let pensionGoal = 0;
    if (goal) {
        achieved = goal <  income;
        pensionGoal = goal;
    }

    const difference = Math.abs(pensionGoal - income);
    const differenceRaw = income - pensionGoal;

    return {
        pensionGoal,
        difference,
        differenceRaw,
        achieved,
    }
}

export function undefinedIfNullOrUndefinedElseValue<T> (value: T | undefined | null){
    if (value === undefined){
        return undefined
    }
 
    if (value === null){
        return undefined
    }

    return value;
}

export const toFullEurosAsCents = (amount?: number) => {
    if (amount === undefined)
        return undefined;
    const asEuros = amount / 100;
    const roundedEuros = Math.ceil(asEuros);
    const asCents = roundedEuros * 100;
    return asCents;
}
